import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
// import HomeIcon from '@material-ui/icons/Home';
import { Homeicon } from '../../assets/icons/CustomIcons';
import ExtensionIcon from '@material-ui/icons/Extension';
import MapIcon from '@material-ui/icons/MyLocation';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import SettingsIcon from '@material-ui/icons/Settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import CampaignIcon from '@material-ui/icons/Announcement';
import StorageIcon from '@material-ui/icons/Storage';
import CategoryIcon from '@material-ui/icons/Category';
import CodeIcon from '@material-ui/icons/Code';
import WebIcon from '@material-ui/icons/Web';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import AssignmentIcon from '@material-ui/icons/Assignment';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import BuildIcon from '@material-ui/icons/Build';
import RoomIcon from '@material-ui/icons/Room';
import PersonIcon from '@material-ui/icons/Person';
import DevicesIcon from '@material-ui/icons/Devices';
import SecurityIcon from '@material-ui/icons/Security';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { ConfluenceChatbox } from '../confluence-chat/ConfluenceChatbox';

const useSidebarLogoStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: 0,
  },
  link: {
    width: '90%',
    margin: '0 auto',
  },
  collapseBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    borderRadius: 4,
    color: theme.palette.common.white,
    opacity: 0.6,
    '&:hover': {
      opacity: 1,
      background: 'rgba(255,255,255,0.12)',
    },
  },
}));

const LogoutButton = () => {
  const identityApi = useApi(identityApiRef);
  return (
    <SidebarItem
      icon={ExitToAppIcon}
      text="Logout"
      onClick={() => identityApi.signOut()}
    />
  );
};

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen, setOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
      {isOpen && (
        <div
          className={classes.collapseBtn}
          onClick={() => setOpen(false)}
          onKeyDown={e => e.key === 'Enter' && setOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Collapse sidebar"
        >
          <ChevronLeftIcon fontSize="small" />
        </div>
      )}
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Discover" icon={<MenuIcon />}>
        <SidebarItem icon={Homeicon} to="/home" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog">
          <SidebarSubmenu title="Catalog">
            <SidebarSubmenuItem
              title="Components"
              to="catalog/kind/component"
              icon={CategoryIcon}
            />
            <SidebarSubmenuItem
              title="Groups"
              to="catalog/kind/group"
              icon={GroupIcon}
            />
            <SidebarSubmenuItem
              title="Locations"
              to="catalog/kind/location"
              icon={RoomIcon}
            />
            <SidebarSubmenuItem
              title="Resources"
              to="catalog/kind/resource"
              icon={StorageIcon}
            />
            <SidebarSubmenuItem
              title="Systems"
              to="catalog/kind/system"
              icon={DevicesIcon}
            />
            <SidebarSubmenuItem
              title="Templates"
              to="catalog/kind/template"
              icon={AssignmentIcon}
            />
            <SidebarSubmenuItem
              title="Users"
              to="catalog/kind/user"
              icon={PersonIcon}
            />
          </SidebarSubmenu>
        </SidebarItem>
        <SidebarItem icon={CodeIcon} to="catalog/backend" text="Backend" />
        <SidebarItem icon={WebIcon} to="catalog/frontend" text="Frontend" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Build" icon={<CreateComponentIcon />}>
        <SidebarItem icon={CreateComponentIcon} to="create" text="Scaffolding" />
        <SidebarItem icon={CampaignIcon} to="announcements" text="Announcements" />
        <SidebarItem icon={BubbleChartIcon} text="AI Analytics">
          <SidebarSubmenu title="AI Analytics">
            <SidebarSubmenuItem icon={TrackChangesIcon} to="test-quality" title="Test Quality" />
            <SidebarSubmenuItem icon={BuildIcon} to="unit-test-generation" title="Unit Test Gen" />
          </SidebarSubmenu>
        </SidebarItem>
        <SidebarItem icon={SecurityIcon} to="trivy" text="Trivy Scanner" />
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Admin"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarItem icon={SettingsIcon} to="settings" text="Settings" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <LogoutButton />
      </SidebarGroup>
    </Sidebar>
    {children}
    <ConfluenceChatbox />
  </SidebarPage>
);
